import { useState, useCallback, useMemo } from 'react';

/**
 * Hook for native Web Share API
 * Falls back to clipboard copy if Share API is not available
 * 
 * @param {Object} defaultOptions - Default share options
 * @returns {Object} Share functionality
 *   - canShare: boolean - Is Share API available
 *   - share: function - Share content
 *   - shareUrl: function - Share URL
 *   - shareText: function - Share text
 *   - shareFile: function - Share file
 */
export default function useShare(defaultOptions = {}) {
  const [error, setError] = useState(null);

  const canShare = useMemo(() => {
    return typeof navigator !== 'undefined' && 'share' in navigator;
  }, []);

  const canShareFiles = useMemo(() => {
    return canShare && navigator.canShare && typeof navigator.canShare === 'function';
  }, [canShare]);

  const share = useCallback(async (options = {}) => {
    setError(null);
    const shareData = { ...defaultOptions, ...options };

    if (!canShare) {
      // Fallback: copy to clipboard
      if (shareData.url) {
        try {
          await navigator.clipboard.writeText(shareData.url);
          return { success: true, method: 'clipboard', message: 'URL copied to clipboard' };
        } catch (err) {
          setError(err);
          return { success: false, method: 'clipboard', error: err.message };
        }
      }
      return { success: false, method: 'none', error: 'Share API not available' };
    }

    try {
      // Check if we can share this data
      if (navigator.canShare && !navigator.canShare(shareData)) {
        return { success: false, method: 'share', error: 'Cannot share this data' };
      }

      await navigator.share(shareData);
      return { success: true, method: 'share', message: 'Shared successfully' };
    } catch (err) {
      // User cancelled or error occurred
      if (err.name === 'AbortError') {
        return { success: false, method: 'share', cancelled: true };
      }
      setError(err);
      return { success: false, method: 'share', error: err.message };
    }
  }, [canShare, defaultOptions]);

  const shareUrl = useCallback(async (url, title, text) => {
    return share({ url, title, text });
  }, [share]);

  const shareText = useCallback(async (text, title) => {
    return share({ text, title });
  }, [share]);

  const shareFile = useCallback(async (files, title, text) => {
    if (!canShareFiles) {
      return { success: false, method: 'none', error: 'File sharing not available' };
    }

    const fileArray = Array.isArray(files) ? files : [files];
    
    if (navigator.canShare && !navigator.canShare({ files: fileArray })) {
      return { success: false, method: 'share', error: 'Cannot share these files' };
    }

    return share({ files: fileArray, title, text });
  }, [canShareFiles, share]);

  return {
    canShare,
    canShareFiles,
    share,
    shareUrl,
    shareText,
    shareFile,
    error,
  };
}


